// ─────────────────────────────────────────────────────────────────────────────
// The Cutting Room — PROTOTYPE (clip player), embedded surface.
//
// Ask the catalog a question; the existing BTB search answers it; the answer's
// own citations become a playable supercut of the public episode videos. Each
// produced cut is saved with an id so the share link replays instantly.
// ─────────────────────────────────────────────────────────────────────────────

import {useRef, useState} from 'react'

import {createCut, watchUrl} from '../lib/cutsClient'
import type {Cut} from '../shared/cuts'
import {JumpPlayer} from './JumpPlayer'

const WAIT_LINES = [
  'reading the catalog…',
  'still reading — this really does read the transcripts…',
  'picking the moments worth your time…',
  'nearly there — cueing the tape…',
]

export function CuttingRoom({
  signedIn,
  getToken,
}: {
  signedIn: boolean
  getToken: () => Promise<string | null>
}) {
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const [waitLine, setWaitLine] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{cut: Cut; saved: boolean} | null>(null)
  const [copied, setCopied] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const timerRef = useRef<number | null>(null)

  async function cutIt() {
    const q = query.trim()
    if (q.length < 3 || busy) return
    setBusy(true)
    setError(null)
    setResult(null)
    setCopied(false)
    setShowAnswer(false)
    setWaitLine(0)
    timerRef.current = window.setInterval(
      () => setWaitLine(n => Math.min(n + 1, WAIT_LINES.length - 1)),
      20_000,
    )
    try {
      setResult(await createCut(q, await getToken()))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'the cut failed')
    } finally {
      if (timerRef.current != null) window.clearInterval(timerRef.current)
      setBusy(false)
    }
  }

  const cut = result?.cut

  return (
    <section id="cutting-room">
      <div className="sec-head">
        <h2 className="disp">The cutting room</h2>
        <div className="sub">
          Ask the whole catalog a question — get the moments themselves, cut into one sitting.
        </div>
      </div>
      <div className="mode-note">
        Prototype. Your question goes to the show’s transcript search (the one thing on this page
        that does send a query), the answer’s citations become timecoded segments, and the player
        below plays those ranges of the public YouTube episodes in place — nothing re-hosted.
        Cutting needs a sign-in — each cut spends real search time — but the cut it makes is
        public: anyone with the link can watch, signed in or not.
      </div>
      {!signedIn ? (
        <p className="notice">Sign in below to make a cut — watching one needs no account.</p>
      ) : null}

      <div className="cutroom-ask">
        <input
          type="text"
          value={query}
          maxLength={300}
          placeholder="e.g. best five business tips across the catalog"
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') void cutIt()
          }}
          disabled={busy}
        />
        <button className="cutroom-go disp" onClick={() => void cutIt()} disabled={busy || query.trim().length < 3}>
          {busy ? 'cutting…' : 'cut it'}
        </button>
      </div>

      {busy ? (
        <p className="notice">
          {WAIT_LINES[waitLine]} A deep cut takes a minute or two — it searches every transcript
          before it picks.
        </p>
      ) : null}
      {error ? <p className="notice notice--warn">{error}</p> : null}

      {cut ? (
        <div className="cutroom-result">
          <div className="cutroom-share">
            {result?.saved ? (
              <>
                <a href={`/watch/${cut.id}`} target="_blank" rel="noreferrer">
                  {watchUrl(cut.id)}
                </a>
                <button
                  onClick={() => {
                    void navigator.clipboard.writeText(watchUrl(cut.id)).then(() => setCopied(true))
                  }}
                >
                  {copied ? 'copied' : 'copy link'}
                </button>
                <span className="cutroom-share-note">
                  share it — anyone can watch, no sign-in
                </span>
              </>
            ) : (
              <span className="cutroom-share-note">
                cut produced but not saved (no storage bound in this environment) — playable here,
                not shareable
              </span>
            )}
          </div>
          {cut.mode === 'search' ? (
            <p className="notice">
              The deep answer wasn’t available just now, so these are the raw strongest search
              matches rather than a curated pick.
            </p>
          ) : null}
          <JumpPlayer segments={cut.segments} />
          {cut.answer ? (
            <div className="cutroom-answer">
              <button className="cutroom-answer-toggle" onClick={() => setShowAnswer(s => !s)}>
                {showAnswer ? 'hide' : 'show'} the written answer this cut grounds
              </button>
              {showAnswer ? <pre>{cut.answer}</pre> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
